import { useMutation, useQuery } from '@apollo/client'
import { AddCircle, Delete, Edit } from '@mui/icons-material'
import { Button, Grid, IconButton, Typography } from '@mui/material'
import { gql } from '__generated__'
import {
    GetCompaniesByNameQuery,
    GetCompanyOfficeLocationsQuery,
} from '__generated__/graphql'
import ToggleEditAutocomplete from 'candidate/components/ToggleEditFormFields/ToggleEditAutocomplete'
import ToggleEditCompaniesAutocomplete from 'candidate/components/ToggleEditFormFields/ToggleEditCompaniesAutocomplete'
import ToggleEditTextField from 'candidate/components/ToggleEditFormFields/ToggleEditTextField'
import {
    EditButtonContainer,
    StyledForm,
    TitleContainer,
} from 'candidate/components/styles'
import { UpdateCandidateAdditionalInfoMutation } from 'candidate/graphql/generatedProfile.gql'
import { CandidateAdditionalInformationOptionsQuery } from 'candidate/graphql/profile.gql'
import isEqual from 'lodash.isequal'
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { isValidPhoneNumber } from 'react-phone-number-input'
import useToast from 'shared/components/Toast/useToast'
import { DropdownOption } from 'shared/models/dropdownOption'
import { getPxAsRem } from 'shared/utils'
import { FormComponentProps, PROFILE_SECTIONS } from './types'

export const GetCompanyOfficeLocations = gql(/* GraphQL */ `
    query GetCompanyOfficeLocations($companyId: ID!) {
        company(id: $companyId) {
            companyOffices {
                id
                officeLocation {
                    value
                    id
                }
            }
        }
    }
`)

export interface AdditionalInformationFormValues {
    company: DropdownOption<
        GetCompaniesByNameQuery['companiesByName']['data'][number]
    >
    sector: DropdownOption
    industry: DropdownOption
    companyOffice: NonNullable<
        GetCompanyOfficeLocationsQuery['company']['companyOffices']
    >[number]
    classYear?: number
    otherAnalysts: string[]
    staffer: {
        firstName: string
        lastName: string
        titleId?: string
        email: string
        phone: string
    }
    tier: DropdownOption
    returnRequest?: boolean
}

const AdditionalInformation: FC<FormComponentProps> = ({
    data,
    loading,
    refetch,
    edit,
    setEdit,
    setUnsavedChangesExist,
    closeEditWithoutModal,
}) => {
    const toast = useToast()
    const additionalInformationOptions = useQuery(
        CandidateAdditionalInformationOptionsQuery
    )

    const [mutate] = useMutation(UpdateCandidateAdditionalInfoMutation)

    const internshipExperience = data.candidate?.mostRecentInternship
    const firstFoundStaffer =
        data.candidate?.staffers && data.candidate?.staffers?.length > 0
            ? data.candidate?.staffers[0].staffer
            : undefined
    const firstIndustryFound =
        internshipExperience?.industries &&
        internshipExperience.industries.length > 0
            ? internshipExperience.industries[0].industry
            : undefined
    const firstSectorFound =
        internshipExperience?.sectors && internshipExperience.sectors.length > 0
            ? internshipExperience.sectors[0].sector
            : undefined
    const otherAnalysts = useMemo(
        () => data.candidate?.otherAnalysts?.map((a) => a.analyst) ?? [],
        [data.candidate]
    )
    let additionalInfoInitialData: AdditionalInformationFormValues = useMemo(
        () => ({
            staffer: {
                firstName: firstFoundStaffer?.firstName ?? '',
                lastName: firstFoundStaffer?.lastName ?? '',
                titleId: firstFoundStaffer?.titleId ?? undefined,
                email: firstFoundStaffer?.email ?? '',
                phone: firstFoundStaffer?.phone ?? '',
            },
            company: {
                id: internshipExperience?.company?.id ?? '',
                value: internshipExperience?.company?.value ?? '',
            },
            companyOffice: {
                id: internshipExperience?.companyOffice?.id ?? '',
                officeLocation: {
                    id:
                        internshipExperience?.companyOffice?.officeLocation
                            ?.id ?? '',
                    value:
                        internshipExperience?.companyOffice?.officeLocation
                            ?.value ?? '',
                },
            },
            industry: {
                id: firstIndustryFound?.id ?? '',
                value: firstIndustryFound?.value ?? '',
            },
            sector: {
                id: firstSectorFound?.id ?? '',
                value: firstSectorFound?.value ?? '',
            },
            returnRequest:
                internshipExperience?.internship?.returnRequest ?? undefined,
            otherAnalysts: otherAnalysts,
            tier: {
                id: data.candidate?.tier?.tier?.id ?? '',
                value: data.candidate?.tier?.tier?.value ?? '',
            },
            classYear: data.candidate?.analystClassYear ?? undefined,
            candidateId: data.candidate?.id,
            country: {
                id: data.candidate?.country?.id ?? '',
                value: data.candidate?.country?.value ?? '',
            },
        }),
        [
            internshipExperience,
            data.candidate,
            firstIndustryFound,
            firstSectorFound,
            firstFoundStaffer,
            otherAnalysts,
        ]
    )

    const [formValues, setFormValues] =
        useState<AdditionalInformationFormValues>(additionalInfoInitialData)

    const [errorValues, setErrorValues] = useState({
        firstName: '',
        lastName: '',
        title: '',
        email: '',
        phone: '',
        company: '',
        industry: '',
        sector: '',
        returnRequest: '',
        otherAnalysts: [''],
        tier: '',
        classYear: '',
        companyOffice: '',
    })

    const handleEditClick = () => {
        if (edit) {
            setEdit(null)
        } else {
            setEdit(PROFILE_SECTIONS.ADDITIONAL_INFORMATION)
        }
    }

    const handleAddAnalyst = () => {
        setFormValues((prev) => ({
            ...prev,
            otherAnalysts: [...prev.otherAnalysts, ''],
        }))
        setErrorValues((prev) => ({
            ...prev,
            otherAnalysts: [...prev.otherAnalysts, ''],
        }))
    }

    const handleRemoveAnalyst = (index) => {
        setFormValues((prev) => {
            const otherAnalysts = [...prev.otherAnalysts]
            otherAnalysts.splice(index, 1)
            return {
                ...prev,
                otherAnalysts,
            }
        })
        setErrorValues((prev) => {
            const otherAnalysts = [...prev.otherAnalysts]
            otherAnalysts.splice(index, 1)
            return {
                ...prev,
                otherAnalysts,
            }
        })
    }

    const resetForm = useCallback(
        () => setFormValues(additionalInfoInitialData),
        [setFormValues, additionalInfoInitialData]
    )

    const handlePhoneChange = (newValue) => {
        setFormValues((prev) => ({
            ...prev,
            staffer: {
                ...prev.staffer,
                phone: newValue ?? '',
            },
        }))
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        try {
            const response = await mutate({
                variables: {
                    staffer: {
                        firstName: formValues.staffer.firstName,
                        lastName: formValues.staffer.lastName,
                        titleId: formValues.staffer.titleId,
                        email: formValues.staffer.email,
                        phone: formValues.staffer.phone ?? '',
                    },
                    internship: {
                        companyOfficeId: formValues.companyOffice.id,
                        companyId: formValues.company?.id ?? '',
                        industryIds: [formValues.industry.id],
                        sectorIds: [formValues.sector.id],
                        internship: {
                            returnRequest: formValues.returnRequest,
                            candidateWorkExperienceId:
                                internshipExperience?.id ?? '',
                        },
                    },
                    otherAnalystsNames: formValues.otherAnalysts.filter(
                        (analyst) => analyst !== ''
                    ),
                    tierId: formValues.tier.id,
                    analystClassYear: formValues.classYear,
                },
            })
            if (response.data?.updateCandidateAdditionalInfo?.success) {
                toast({
                    title: 'Success!',
                    message: 'Additional information has been updated',
                    severity: 'success',
                })
                closeEditWithoutModal()
                refetch()
            } else {
                toast({
                    title: 'Error',
                    message: 'Something went wrong. Please try again.',
                    severity: 'error',
                })
            }
        } catch (e) {
            console.log(e)
            toast({
                title: 'Error',
                message: 'Something went wrong. Please try again.',
                severity: 'error',
            })
        }
    }

    const formatReturnRequestValue = (value: boolean | undefined) => {
        if (value === true) return 'Yes'
        if (value === false) return 'No'
        return ''
    }

    const unsavedChangesExist = useMemo(() => {
        if (
            !isEqual(formValues, {
                ...additionalInfoInitialData,
            })
        )
            return true
        return false
    }, [formValues, additionalInfoInitialData])

    const isDisabled = useMemo(() => {
        if (!unsavedChangesExist) return true
        const hasEmptyValues = Object.values(formValues).some((value) => {
            return (
                value === '' ||
                isEqual(value, { id: '', value: '' }) ||
                isEqual(value, ['']) ||
                isEqual(value, {
                    id: '',
                    officeLocation: { id: '', value: '' },
                })
            )
        })
        if (hasEmptyValues) return true
        const hasErrors = Object.values(errorValues).some((value) => {
            if (Array.isArray(value)) {
                return value.some((v) => v !== '')
            }
            return value !== ''
        })
        return hasErrors
    }, [formValues, errorValues, unsavedChangesExist])

    useEffect(
        () => setUnsavedChangesExist(unsavedChangesExist),
        [unsavedChangesExist, setUnsavedChangesExist]
    )

    useEffect(() => {
        if (!edit) resetForm()
    }, [edit, resetForm])

    const { data: companyData } = useQuery(GetCompanyOfficeLocations, {
        variables: {
            companyId: formValues.company?.id,
        },
    })

    return (
        <>
            <TitleContainer>
                <Typography variant="h6" component="h2">
                    Additional Information
                </Typography>
                <IconButton onClick={handleEditClick} disabled={loading}>
                    <Edit fontSize="small" />
                </IconButton>
            </TitleContainer>
            <StyledForm onSubmit={handleSubmit}>
                <Grid container rowSpacing={3} columnSpacing={2}>
                    <Grid item xs={12} md={6}>
                        <ToggleEditCompaniesAutocomplete
                            inCandidatePortal
                            edit={edit}
                            size="medium"
                            helperText={errorValues.company}
                            error={!!errorValues.company}
                            label="Final Internship Company"
                            onChange={(event, newValue) => {
                                if (!Array.isArray(newValue)) {
                                    if (newValue !== formValues?.company) {
                                        formValues.companyOffice = {
                                            id: '',
                                            officeLocation: {
                                                id: '',
                                                value: '',
                                            },
                                        }
                                    }
                                    setFormValues((prevState) => ({
                                        ...prevState,
                                        company: newValue || {
                                            id: '',
                                            value: '',
                                        },
                                    }))
                                }
                            }}
                            labelId="company"
                            value={formValues?.company}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <ToggleEditAutocomplete
                            edit={edit}
                            helperText={errorValues.industry}
                            error={!!errorValues.industry}
                            label="Internship Industry"
                            options={
                                additionalInformationOptions.data?.industries ||
                                []
                            }
                            onChange={(event, newValue) => {
                                if (!Array.isArray(newValue)) {
                                    setFormValues((prevState) => ({
                                        ...prevState,
                                        industry: newValue || {
                                            id: '',
                                            value: '',
                                        },
                                    }))
                                }
                            }}
                            labelId="industry"
                            value={
                                formValues?.industry || {
                                    id: '',
                                    value: '',
                                }
                            }
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <ToggleEditAutocomplete
                            edit={edit}
                            helperText={errorValues.sector}
                            error={!!errorValues.sector}
                            label="Internship Sector"
                            options={
                                additionalInformationOptions.data?.sectors || []
                            }
                            onChange={(event, newValue) => {
                                if (!Array.isArray(newValue)) {
                                    setFormValues((prevState) => ({
                                        ...prevState,
                                        sector: newValue || {
                                            id: '',
                                            value: '',
                                        },
                                    }))
                                }
                            }}
                            labelId="sector"
                            value={
                                formValues?.sector || {
                                    id: '',
                                    value: '',
                                }
                            }
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <ToggleEditAutocomplete
                            edit={edit}
                            helperText={errorValues.companyOffice}
                            error={!!errorValues.companyOffice}
                            label="Internship Office Location"
                            disabled={!formValues.company}
                            options={
                                companyData?.company.companyOffices
                                    ?.map((co) => co.officeLocation)
                                    .filter(
                                        (val) =>
                                            val !== undefined && val !== null
                                    ) ?? []
                            }
                            onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                                if (!e.target.value) {
                                    setErrorValues((prev) => ({
                                        ...prev,
                                        companyOffice:
                                            'Office Location is required',
                                    }))
                                }
                            }}
                            onChange={(event, newValue) => {
                                if (newValue && !Array.isArray(newValue)) {
                                    if (newValue) {
                                        setErrorValues((prev) => ({
                                            ...prev,
                                            companyOffice: '',
                                        }))
                                    } else {
                                        setErrorValues((prev) => ({
                                            ...prev,
                                            companyOffice:
                                                'Office Location is required',
                                        }))
                                    }

                                    const newCompanyOffice =
                                        companyData?.company.companyOffices?.find(
                                            (co) =>
                                                co.officeLocation?.id ===
                                                newValue?.id
                                        )
                                    if (newCompanyOffice) {
                                        setFormValues((prevState) => ({
                                            ...prevState,
                                            companyOffice: newCompanyOffice,
                                        }))
                                    }
                                }
                            }}
                            labelId="officeLocation"
                            value={
                                formValues.companyOffice.officeLocation ?? null
                            }
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <ToggleEditAutocomplete
                            edit={edit}
                            label="Internship Return Request"
                            options={[
                                { value: 'Yes', id: 'true' },
                                { value: 'No', id: 'false' },
                            ]}
                            onChange={(event, newValue) => {
                                if (!Array.isArray(newValue)) {
                                    setFormValues((prevState) => ({
                                        ...prevState,
                                        returnRequest: newValue?.id === 'true',
                                    }))
                                }
                            }}
                            helperText={errorValues.returnRequest}
                            error={!!errorValues.returnRequest}
                            labelId="returnRequest"
                            value={{
                                id: formValues?.returnRequest?.toString() || '',
                                value: formatReturnRequestValue(
                                    formValues?.returnRequest
                                ),
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <ToggleEditTextField
                            edit={edit}
                            label="Analyst Class Year"
                            name="classYear"
                            onBlur={(e) => {
                                if (!e.target.value) {
                                    setErrorValues((prev) => ({
                                        ...prev,
                                        classYear: 'Class year is required',
                                    }))
                                }
                            }}
                            value={formValues.classYear?.toString() || ''}
                            helperText={errorValues.classYear}
                            error={!!errorValues.classYear}
                            onChange={(e) => {
                                const numberValue = +e.target.value
                                if (isNaN(numberValue)) {
                                    return
                                } else {
                                    setFormValues((prev) => ({
                                        ...prev,
                                        classYear: numberValue,
                                    }))
                                    setErrorValues((prev) => ({
                                        ...prev,
                                        classYear: '',
                                    }))
                                }
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <ToggleEditAutocomplete
                            edit={edit}
                            helperText={errorValues.tier}
                            error={!!errorValues.tier}
                            label="Most Recent Tier/Ranking"
                            options={
                                additionalInformationOptions.data?.tiers || []
                            }
                            onBlur={() => {}}
                            onChange={(event, newValue) => {
                                if (!Array.isArray(newValue)) {
                                    setFormValues((prevState) => ({
                                        ...prevState,
                                        tier: newValue || { id: '', value: '' },
                                    }))
                                }
                            }}
                            labelId="tier"
                            value={
                                formValues?.tier || {
                                    id: '',
                                    value: '',
                                }
                            }
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <ToggleEditTextField
                            edit={edit}
                            label="Staffer First Name"
                            name="firstName"
                            onBlur={() => {}}
                            value={formValues?.staffer?.firstName}
                            helperText={errorValues.firstName}
                            error={!!errorValues.firstName}
                            onChange={(e) => {
                                setFormValues((prev) => ({
                                    ...prev,
                                    staffer: {
                                        ...prev.staffer,
                                        firstName: e.target.value,
                                    },
                                }))
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <ToggleEditTextField
                            edit={edit}
                            label="Staffer Last Name"
                            name="lastName"
                            onBlur={() => {}}
                            value={formValues?.staffer?.lastName}
                            helperText={errorValues.lastName}
                            error={!!errorValues.lastName}
                            onChange={(e) => {
                                setFormValues((prev) => ({
                                    ...prev,
                                    staffer: {
                                        ...prev.staffer,
                                        lastName: e.target.value,
                                    },
                                }))
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <ToggleEditAutocomplete
                            edit={edit}
                            label="Staffer Title"
                            labelId="stafferTitle"
                            options={
                                additionalInformationOptions.data?.titles || []
                            }
                            value={additionalInformationOptions.data?.titles.find(
                                (title) =>
                                    title.id === formValues?.staffer?.titleId
                            )}
                            helperText={errorValues.title}
                            error={!!errorValues.title}
                            onChange={(event, newValue) => {
                                if (!Array.isArray(newValue)) {
                                    setFormValues((prev) => ({
                                        ...prev,
                                        staffer: {
                                            ...prev.staffer,
                                            titleId: newValue?.id,
                                        },
                                    }))
                                }
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <ToggleEditTextField
                            edit={edit}
                            label="Staffer Email"
                            name="email"
                            onBlur={() => {}}
                            value={formValues?.staffer?.email}
                            helperText={errorValues.email}
                            error={!!errorValues.email}
                            onChange={(e) => {
                                setFormValues((prev) => ({
                                    ...prev,
                                    staffer: {
                                        ...prev.staffer,
                                        email: e.target.value,
                                    },
                                }))
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <ToggleEditTextField
                            edit={edit}
                            label="Staffer Phone"
                            name="phone"
                            mask="phone"
                            helperText={errorValues.phone}
                            error={!!errorValues.phone}
                            onBlur={(e) => {
                                if (
                                    e.target.value &&
                                    !isValidPhoneNumber(
                                        formValues.staffer.phone
                                    )
                                ) {
                                    setErrorValues((prev) => ({
                                        ...prev,
                                        phone: 'Staffer Phone is invalid',
                                    }))
                                }
                            }}
                            value={formValues?.staffer?.phone ?? ''}
                            onChange={handlePhoneChange}
                        />
                    </Grid>
                    {formValues.otherAnalysts?.map((analyst, index) => (
                        <Grid item xs={12} md={6} key={index}>
                            <ToggleEditTextField
                                edit={edit}
                                label="Other Analyst in Group"
                                name="otherAnalysts"
                                onBlur={() => {}}
                                InputProps={{
                                    endAdornment: (
                                        <IconButton
                                            onClick={() =>
                                                handleRemoveAnalyst(index)
                                            }
                                            sx={{ marginRight: getPxAsRem(2) }}
                                        >
                                            <Delete />
                                        </IconButton>
                                    ),
                                }}
                                value={analyst || ''}
                                helperText={errorValues.otherAnalysts[index]}
                                error={!!errorValues.otherAnalysts[index]}
                                onChange={(e) => {
                                    setFormValues((prev) => {
                                        const otherAnalysts = [
                                            ...prev.otherAnalysts,
                                        ]
                                        otherAnalysts?.splice(
                                            index,
                                            1,
                                            e.target.value
                                        )
                                        return {
                                            ...prev,
                                            otherAnalysts,
                                        }
                                    })
                                    if (e.target.value) {
                                        setErrorValues((prev) => {
                                            const otherAnalysts = [
                                                ...prev.otherAnalysts,
                                            ]
                                            otherAnalysts.splice(index, 1, '')
                                            return {
                                                ...prev,
                                                otherAnalysts,
                                            }
                                        })
                                    }
                                }}
                            />
                        </Grid>
                    ))}
                    {edit && (
                        <EditButtonContainer item xs={12}>
                            <Button
                                onClick={handleAddAnalyst}
                                variant="text"
                                startIcon={<AddCircle />}
                            >
                                Add Analyst
                            </Button>
                        </EditButtonContainer>
                    )}

                    {edit && (
                        <EditButtonContainer item xs={12}>
                            <Button
                                variant="outlined"
                                type="reset"
                                onClick={() => {
                                    closeEditWithoutModal()
                                    resetForm()
                                }}
                            >
                                Cancel
                            </Button>
                            <Button type="submit" disabled={isDisabled}>
                                Save and Update
                            </Button>
                        </EditButtonContainer>
                    )}
                </Grid>
            </StyledForm>
        </>
    )
}

export default AdditionalInformation
